/**
 * Font Service
 * 
 * Handles dynamic loading of Google Fonts for web and native platforms.
 * Supports caching to prevent duplicate font loading.
 */

import { Platform } from 'react-native';

class FontService {
    private loadedFonts: Set<string> = new Set();
    private loadingPromises: Map<string, Promise<boolean>> = new Map();

    /**
     * Load a Google Font dynamically
     * @param fontName - The Google Font family name (e.g., 'Roboto', 'Open Sans')
     *                   Pass null to use local fonts (no loading needed)
     * @returns Promise<boolean> - Whether the font was loaded successfully
     */
    async loadFont(fontName: string | null | undefined): Promise<boolean> {
        // Skip if null/undefined - means use local font
        if (!fontName) {
            console.log('🔤 [FontService] Using local font (null/undefined passed)');
            return true;
        }

        // Skip if it's the default local font name
        if (fontName === 'primaryFont' || fontName === 'logoFont') {
            return true;
        }

        // Return if already loaded
        if (this.loadedFonts.has(fontName)) {
            console.log(`🔤 [FontService] Font already loaded: ${fontName}`);
            return true;
        }

        // Return existing promise if currently loading
        if (this.loadingPromises.has(fontName)) {
            return this.loadingPromises.get(fontName)!;
        }

        const loadPromise = this.doLoadFont(fontName);
        this.loadingPromises.set(fontName, loadPromise);

        try {
            const result = await loadPromise;
            if (result) {
                this.loadedFonts.add(fontName);
            }
            return result;
        } finally {
            this.loadingPromises.delete(fontName);
        }
    }

    private async doLoadFont(fontName: string): Promise<boolean> {
        if (Platform.OS === 'web') {
            return this.loadFontWeb(fontName);
        } else {
            return this.loadFontNative(fontName);
        }
    }

    /**
     * Load font for web platform by injecting a Google Fonts stylesheet
     */
    private async loadFontWeb(fontName: string): Promise<boolean> {
        try {
            // Build Google Fonts URL with common weights
            const weights = '400;500;600;700';
            const fontUrl = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontName)}:wght@${weights}&display=swap`;

            // Check if link already exists
            const existingLink = document.querySelector(`link[href*="${encodeURIComponent(fontName)}"]`);
            if (existingLink) {
                console.log(`🔤 [FontService] Font link already exists: ${fontName}`);
                this.updateCSSVariables(fontName);
                return true;
            }

            // Create and inject link element
            const link = document.createElement('link');
            link.href = fontUrl;
            link.rel = 'stylesheet';
            link.crossOrigin = 'anonymous';

            // Wait for font to load
            await new Promise<void>((resolve, reject) => {
                link.onload = () => resolve();
                link.onerror = () => reject(new Error(`Failed to load font: ${fontName}`));
                document.head.appendChild(link);
            });

            // Update CSS custom properties
            this.updateCSSVariables(fontName);

            return true;
        } catch (error) {
            console.error(`❌ [FontService] Failed to load font: ${fontName}`, error);
            return false;
        }
    }

    /**
     * Update CSS custom properties with the new font
     */
    private updateCSSVariables(fontName: string): void {
        const fontStack = `'${fontName}', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif`;
        document.documentElement.style.setProperty('--primary-font', fontStack);
        
        // Also update body font directly for immediate effect
        document.body.style.fontFamily = fontStack;
    }

    /**
     * Load font for React Native
     * Note: This is a simplified implementation. For full native support,
     * you may need to use @expo-google-fonts packages or expo-font URL loading.
     */
    private async loadFontNative(fontName: string): Promise<boolean> {
        try {
            // For React Native, we use expo-font's URL loading capability
            // This works for web but may have limitations on native
            const Font = require('expo-font');
            
            // Convert font name to URL-safe format
            const fontSlug = fontName.toLowerCase().replace(/\s+/g, '');
            const fontUrl = `https://fonts.gstatic.com/s/${fontSlug}/v1/${fontName.replace(/\s+/g, '')}-Regular.ttf`;
            
            await Font.loadAsync({
                [fontName]: { uri: fontUrl }
            });

            console.log(`✅ [FontService] Loaded native font: ${fontName}`);
            return true;
        } catch (error) {
            console.warn(`⚠️ [FontService] Native font loading failed for ${fontName}, using fallback. Error:`, error);
            // Don't fail - let the app use fallback fonts
            return false;
        }
    }

    /**
     * Check if a font is loaded
     */
    isLoaded(fontName: string): boolean {
        return this.loadedFonts.has(fontName);
    }

    /**
     * Get CSS font-family string for a font
     */
    getFontFamily(fontName: string): string {
        return `'${fontName}', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif`;
    }

    /**
     * Preload multiple fonts
     */
    async preloadFonts(fontNames: string[]): Promise<void> {
        await Promise.all(fontNames.map(name => this.loadFont(name)));
    }

    /**
     * Get list of loaded fonts
     */
    getLoadedFonts(): string[] {
        return Array.from(this.loadedFonts);
    }
}

export const fontService = new FontService();
export default fontService;
